import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Upload,
  TreeSelect,
  Radio,
  Space,
  message,
  Divider,
  Typography,
  Image
} from 'antd';
import {
  SaveOutlined,
  UploadOutlined,
  FileTextOutlined,
  FileImageOutlined,
  VideoCameraOutlined,
  LinkOutlined,
  AppstoreOutlined,
  DeleteOutlined,
  PlayCircleOutlined
} from '@ant-design/icons';
import api from '../services/api';
import MaterialSelector from './MaterialSelector';

const { Title, Text } = Typography;
const { TextArea } = Input;

const MaterialUploadModal = ({ 
  visible, 
  onCancel, 
  onSuccess, 
  initialType = 'TEXT',
  hideTypeSelection = false,
  tenantId 
}) => {
  const [materialType, setMaterialType] = useState(initialType);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [groupTree, setGroupTree] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [coverFileList, setCoverFileList] = useState([]);
  const [previewUrls, setPreviewUrls] = useState({});

  // 封面素材选择器状态
  const [coverSelectorVisible, setCoverSelectorVisible] = useState(false);
  const [selectedCoverMaterial, setSelectedCoverMaterial] = useState(null);

  // 校验小程序标题字符长度（最多20个字）
  const validateMiniprogramTitle = async (_, value) => {
    const val = (value || '').trim();
    if (!val) {
      return Promise.reject('请输入小程序消息标题');
    }
    if (val.length > 20) {
      return Promise.reject('标题最多20个字');
    }
    return Promise.resolve();
  };

  // 素材类型配置（复用MaterialEdit的配置）
  const materialTypes = [
    { value: 'TEXT', label: '文字', icon: <FileTextOutlined />, color: 'blue' },
    { value: 'IMAGE', label: '图片', icon: <FileImageOutlined />, color: 'green' },
    { value: 'VIDEO', label: '视频', icon: <VideoCameraOutlined />, color: 'purple' },
    { value: 'LINK', label: '链接', icon: <LinkOutlined />, color: 'orange' },
    { value: 'MINIPROGRAM', label: '小程序', icon: <AppstoreOutlined />, color: 'red' }
  ];

  const currentTypeConfig = materialTypes.find(t => t.value === materialType);

  // 加载分组树（复用MaterialEdit的逻辑）
  const loadGroupTree = async () => {
    if (!tenantId) {
      return;
    }
    
    try {
      const response = await api.get('/material-groups/tree', {
        params: { tenantId }
      });
      if (response.data.success) {
        setGroupTree(convertToTreeSelectData(response.data.data));
      }
    } catch (error) {
      message.error('加载分组失败');
    }
  };

  // 转换分组数据为TreeSelect格式（复用MaterialEdit的逻辑）
  const convertToTreeSelectData = (groups) => {
    if (!groups || groups.length === 0) return [];
    
    const realGroups = groups.filter(group => 
      group && group.id !== null && group.id !== -1 && typeof group.id === 'number'
    );
    
    return realGroups.map(group => ({
      value: group.id,
      title: group.name,
      children: group.children ? convertToTreeSelectData(group.children) : []
    }));
  };

  // 初始化
  useEffect(() => {
    if (visible) {
      loadGroupTree();
      form.resetFields();
      setFileList([]);
      setCoverFileList([]);
      setPreviewUrls({});
      setMaterialType(initialType);
    }
  }, [visible, tenantId, initialType]);

  // 清理预览URL（复用MaterialEdit的逻辑）
  useEffect(() => {
    return () => {
      coverFileList.forEach(file => {
        if (file.url && file.url.startsWith('blob:')) {
          URL.revokeObjectURL(file.url);
        }
        if (file.thumbUrl && file.thumbUrl.startsWith('blob:')) {
          URL.revokeObjectURL(file.thumbUrl);
        }
      });
      
      fileList.forEach(file => {
        if (file.url && file.url.startsWith('blob:')) {
          URL.revokeObjectURL(file.url);
        }
        if (file.thumbUrl && file.thumbUrl.startsWith('blob:')) {
          URL.revokeObjectURL(file.thumbUrl);
        }
      });
    };
  }, [coverFileList, fileList]);

  // 文件上传配置（复用MaterialEdit的逻辑）
  const uploadProps = {
    beforeUpload: (file) => {
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        let errorMsg = '文件大小超过限制';
        if (materialType === 'IMAGE') {
          errorMsg = '图片大小不能超过10MB，请压缩后重试';
        } else if (materialType === 'VIDEO') {
          errorMsg = '视频大小不能超过10MB，请压缩后重试';
        }
        message.error(errorMsg);
        return Upload.LIST_IGNORE;
      }
      
      return false;
    },
    onRemove: (file) => {
      const newFileList = fileList.filter(item => item.uid !== file.uid);
      setFileList(newFileList);
    },
    fileList,
    onChange: ({ fileList: newFileList }) => {
      const processedFileList = newFileList.map(file => {
        if (file.originFileObj && !file.url && !file.thumbUrl) {
          const localUrl = URL.createObjectURL(file.originFileObj);
          return {
            ...file,
            url: localUrl,
            thumbUrl: localUrl
          };
        }
        return file;
      });
      
      setFileList(processedFileList);
    }
  };

  // 封面图片上传配置（复用MaterialEdit的逻辑）
  const coverUploadProps = {
    beforeUpload: (file) => {
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        message.error('小程序封面图片大小不能超过10MB，请压缩后重试');
        return Upload.LIST_IGNORE;
      }
      
      if (!file.type.startsWith('image/')) {
        message.error('只支持图片格式的封面');
        return Upload.LIST_IGNORE;
      }
      
      return false;
    },
    onRemove: () => {
      setCoverFileList([]);
      setPreviewUrls(prev => ({
        ...prev,
        miniprogramCover: null
      }));
    },
    fileList: coverFileList,
    onChange: ({ fileList: newFileList }) => {
      const processedFileList = newFileList.map(file => {
        if (file.originFileObj && !file.url && !file.thumbUrl) {
          const localUrl = URL.createObjectURL(file.originFileObj);
          return {
            ...file,
            url: localUrl,
            thumbUrl: localUrl
          };
        }
        return file;
      });
      
      setCoverFileList(processedFileList);
      
      if (processedFileList.length > 0 && processedFileList[0].url) {
        setPreviewUrls(prev => ({
          ...prev,
          miniprogramCover: processedFileList[0].url
        }));
      } else {
        setPreviewUrls(prev => ({
          ...prev,
          miniprogramCover: null
        }));
      }
    }
  };

  // 处理链接封面素材选择
  const handleSelectCoverMaterial = async (material) => {
    try {
      console.log('选择封面素材:', material);

      // 调用后端API获取适配消息场景的格式
      const response = await api.get(`/materials/${material.id}/wework-format/message`);

      if (response.data.success) {
        const weworkData = response.data.data;
        console.log('获取企微格式成功:', weworkData);

        // 设置表单字段值
        form.setFieldsValue({
          linkPicUrl: weworkData.image?.url || material.content?.image?.url,
          linkPicMediaId: weworkData.image?.mediaId
        });

        setSelectedCoverMaterial(material);
        setCoverSelectorVisible(false);
        message.success('已设置封面图片');
      } else {
        message.error('获取素材数据失败');
      }
    } catch (error) {
      console.error('设置封面失败:', error);
      message.error('设置封面失败，请重试');
    }
  };

  // 提交表单（复用MaterialEdit的逻辑，但简化为仅创建模式）
  const handleSubmit = async (values) => {
    if (!tenantId) {
      message.error('未找到租户信息，请重新登录');
      return;
    }
    
    setLoading(true);
    
    try {
      let response;
      const submitData = {
        ...values,
        tenantId,
        materialType
      };

      // 根据素材类型构建不同的请求（复用MaterialEdit的逻辑）
      switch (materialType) {
        case 'TEXT':
          submitData.textContent = values.textContent;
          response = await api.post('/materials/text', submitData);
          break;

        case 'IMAGE':
          if (fileList.length === 0) {
            message.error('请选择图片文件');
            setLoading(false);
            return;
          }
          
          if (fileList.length === 0 || !fileList.some(file => file.originFileObj)) {
            message.error('请选择图片文件');
            setLoading(false);
            return;
          }
          
          const batchFormData = new FormData();
          
          fileList.forEach(file => {
            if (file.originFileObj) {
              batchFormData.append('files', file.originFileObj);
            }
          });
          
          batchFormData.append('tenantId', tenantId);
          if (values.groupId) {
            batchFormData.append('groupId', values.groupId);
          }
          if (values.description) {
            batchFormData.append('description', values.description);
          }
          
          response = await api.post('/materials/image/batch', batchFormData, {
            headers: {
              'Content-Type': undefined
            }
          });
          
          if (response.data.success) {
            const { successCount, failCount, results } = response.data.data;
            
            if (failCount === 0) {
              message.success(`成功创建 ${successCount} 个图片素材`);
            } else {
              message.warning(`成功创建 ${successCount} 个素材，${failCount} 个失败`);
              
              results.filter(result => !result.success).forEach(result => {
                message.error(`${result.fileName}: ${result.error}`, 3);
              });
            }
            
            if (onSuccess) onSuccess();
            onCancel();
            return;
          } else {
            message.error(response.data.message || '批量创建失败');
            setLoading(false);
            return;
          }
          break;

        case 'VIDEO':
          if (fileList.length === 0) {
            message.error('请选择视频文件');
            setLoading(false);
            return;
          }
          
          const videoFormData = new FormData();
          videoFormData.append('material', new Blob([JSON.stringify(submitData)], {type: 'application/json'}));
          if (fileList.length > 0 && fileList[0].originFileObj) {
            videoFormData.append('file', fileList[0].originFileObj);
          }
          
          response = await api.post('/materials/video', videoFormData, {
            headers: {
              'Content-Type': undefined
            }
          });
          break;

        case 'LINK':
          submitData.linkInfo = {
            title: values.linkTitle,
            url: values.linkUrl,
            description: values.linkDescription,
            picUrl: values.linkPicUrl
          };
          
          response = await api.post('/materials/link', submitData);
          break;

        case 'MINIPROGRAM':
          // 创建小程序素材时必须有封面图
          if (coverFileList.length === 0 || !coverFileList.some(file => file.originFileObj)) {
            message.error('创建小程序素材时必须选择封面图，否则发送会失败');
            setLoading(false);
            return;
          }
          
          submitData.miniprogramInfo = {
            title: values.miniprogramTitle,
            appid: values.miniprogramAppid,
            page: values.miniprogramPage,
            description: values.miniprogramDescription
          };
          
          const miniprogramFormData = new FormData();
          miniprogramFormData.append('material', new Blob([JSON.stringify(submitData)], {type: 'application/json'}));
          
          if (coverFileList.length > 0 && coverFileList[0].originFileObj) {
            miniprogramFormData.append('coverFile', coverFileList[0].originFileObj);
          }
          
          response = await api.post('/materials/miniprogram', miniprogramFormData, {
            headers: {
              'Content-Type': undefined
            }
          });
          break;
      }

      if (response?.data?.success) {
        message.success('创建成功');
        if (onSuccess) onSuccess();
        onCancel();
      } else {
        message.error(response?.data?.message || '操作失败');
      }
    } catch (error) {
      console.error('提交失败:', error);
      message.error('操作失败');
    } finally {
      setLoading(false);
    }
  };

  // 渲染不同类型的表单内容（复用MaterialEdit的逻辑）
  const renderContentForm = () => {
    switch (materialType) {
      case 'TEXT':
        return (
          <Form.Item
            name="textContent"
            label="文字内容"
            rules={[{ required: true, message: '请输入文字内容' }]}
          >
            <TextArea 
              rows={6} 
              placeholder="请输入文字内容..."
              showCount
              maxLength={2000}
            />
          </Form.Item>
        );

      case 'IMAGE':
        return (
          <Form.Item
            label="图片文件"
            required
            extra="支持JPG、PNG格式，文件大小不超过10MB，可同时选择多张图片批量创建，将自动使用文件名作为素材名称"
          >
            <Upload
              {...uploadProps}
              accept="image/*"
              multiple
              listType="picture-card"
            >
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>选择图片</div>
              </div>
            </Upload>
          </Form.Item>
        );

      case 'VIDEO':
        return (
          <Form.Item
            label="视频文件"
            required
            extra="支持MP4格式，文件大小不超过10MB"
          >
            <Upload
              {...uploadProps}
              accept="video/*"
              maxCount={1}
              listType="picture-card"
              itemRender={(originNode, file, fileList, actions) => {
                const videoUrl = file.url || file.thumbUrl;
                return (
                  <div style={{ 
                    position: 'relative', 
                    width: '104px', 
                    height: '104px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    background: '#f5f5f5'
                  }}>
                    {videoUrl ? (
                      <>
                        <video
                          src={videoUrl}
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover' 
                          }}
                          muted
                          controls={false}
                        />
                        <div style={{ 
                          position: 'absolute', 
                          top: 0, 
                          left: 0, 
                          right: 0, 
                          bottom: 0, 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          background: 'rgba(0,0,0,0.3)'
                        }}>
                          <PlayCircleOutlined style={{ fontSize: '24px', color: '#fff' }} />
                        </div>
                      </>
                    ) : (
                      <div style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%'
                      }}>
                        <VideoCameraOutlined style={{ fontSize: '24px', color: '#666', marginBottom: '8px' }} />
                        <div style={{ fontSize: '12px', color: '#666', textAlign: 'center', padding: '0 4px' }}>
                          {file.name}
                        </div>
                      </div>
                    )}
                    
                    <div style={{ 
                      position: 'absolute', 
                      top: '4px', 
                      right: '4px',
                      background: 'rgba(0,0,0,0.5)',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={actions.remove}>
                      <DeleteOutlined style={{ fontSize: '12px', color: 'white' }} />
                    </div>
                  </div>
                );
              }}
            >
              {fileList.length < 1 && (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>选择视频</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        );

      case 'LINK':
        return (
          <>
            <Form.Item
              name="linkTitle"
              label="图文消息标题"
              rules={[
                { required: true, message: '请输入图文消息标题' },
                { max: 128, message: '标题最长为128字节' }
              ]}
            >
              <Input placeholder="请输入图文消息标题..." />
            </Form.Item>
            
            <Form.Item
              name="linkUrl"
              label="图文消息链接"
              rules={[
                { required: true, message: '请输入图文消息链接' },
                { type: 'url', message: '请输入有效的URL地址' }
              ]}
            >
              <Input placeholder="https://..." />
            </Form.Item>

            {/* 隐藏字段：存储封面URL */}
            <Form.Item name="linkPicUrl" hidden>
              <Input />
            </Form.Item>

            {/* 隐藏字段：存储封面media_id */}
            <Form.Item name="linkPicMediaId" hidden>
              <Input />
            </Form.Item>

            {/* 封面图片选择 */}
            <Form.Item label="图文消息封面" required>
              {selectedCoverMaterial || form.getFieldValue('linkPicUrl') ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px', border: '1px solid #d9d9d9', borderRadius: 4 }}>
                  <Image
                    src={form.getFieldValue('linkPicUrl')}
                    width={80}
                    height={80}
                    style={{ objectFit: 'cover', borderRadius: 4 }}
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12 }}>
                      {selectedCoverMaterial?.name || '已选择封面图'}
                    </div>
                  </div>
                  <Button
                    size="small"
                    onClick={() => setCoverSelectorVisible(true)}
                  >
                    重新选择
                  </Button>
                </div>
              ) : (
                <Button
                  type="dashed"
                  icon={<FileImageOutlined />}
                  onClick={() => setCoverSelectorVisible(true)}
                  block
                  style={{ height: 80 }}
                >
                  从素材库选择封面图片（必选）
                </Button>
              )}
            </Form.Item>

            <Form.Item
              name="linkDescription"
              label="图文消息描述（可选）"
              rules={[{ max: 512, message: '描述最长为512字节' }]}
            >
              <TextArea rows={3} placeholder="请输入图文消息描述..." />
            </Form.Item>
          </>
        );

      case 'MINIPROGRAM':
        return (
          <>
            <Form.Item
              name="miniprogramTitle"
              label="小程序消息标题"
              rules={[{ validator: validateMiniprogramTitle }]}
              getValueFromEvent={(e) => {
                const value = e?.target?.value || '';
                return value.slice(0, 20);
              }}
            >
              <Input 
                placeholder="请输入小程序消息标题..."
                showCount
                maxLength={20}
              />
            </Form.Item>
            
            <Form.Item
              name="miniprogramAppid"
              label="小程序AppID"
              rules={[{ required: true, message: '请输入小程序AppID' }]}
            >
              <Input placeholder="请输入小程序AppID..." />
            </Form.Item>
            
            <Form.Item
              name="miniprogramPage"
              label="小程序页面路径"
              rules={[{ required: true, message: '请输入小程序页面路径' }]}
            >
              <Input 
                placeholder="/pages/index" 
                onChange={(e) => {
                  let value = e.target.value;
                  if (value && !value.startsWith('/')) {
                    value = '/' + value;
                  }
                  form.setFieldsValue({ miniprogramPage: value });
                }}
              />
            </Form.Item>
            
            <Form.Item
              label="小程序消息封面图片"
              required
              extra="创建小程序素材时必须选择封面图，建议尺寸为520*416，支持JPG、PNG格式，文件大小不超过10MB"
            >
              <Upload
                {...coverUploadProps}
                accept="image/*"
                maxCount={1}
                listType="picture-card"
              >
                {coverFileList.length < 1 && (
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>选择封面</div>
                  </div>
                )}
              </Upload>
            </Form.Item>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <>
    <Modal
      title={hideTypeSelection ? `创建${currentTypeConfig?.label}素材` : "创建素材"}
      visible={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          取消
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          loading={loading}
          icon={<SaveOutlined />}
          onClick={() => form.submit()}
        >
          创建素材
        </Button>
      ]}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          materialType
        }}
      >
        {/* 素材类型选择 */}
        {!hideTypeSelection && (
          <>
            <Form.Item
              label="素材类型"
              style={{ marginBottom: 16 }}
            >
              <Radio.Group 
                value={materialType} 
                onChange={(e) => {
                  setMaterialType(e.target.value);
                  setFileList([]);
                  setCoverFileList([]);
                  setPreviewUrls({});
                  form.resetFields();
                }}
                buttonStyle="solid"
              >
                {materialTypes.map(type => (
                  <Radio.Button key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </Form.Item>

            <Divider />
          </>
        )}

        {/* 基础信息 */}
        <Title level={5}>基础信息</Title>
        
        {materialType !== 'IMAGE' && (
          <Form.Item
            name="name"
            label="素材名称"
            rules={[{ required: true, message: '请输入素材名称' }]}
          >
            <Input placeholder="请输入素材名称..." />
          </Form.Item>
        )}
        
        <Form.Item
          name="groupId"
          label="所属分组"
        >
          <TreeSelect
            placeholder="请选择分组（可选）"
            allowClear
            showSearch
            treeNodeFilterProp="title"
            filterTreeNode={(inputValue, treeNode) => {
              const title = treeNode.title ? treeNode.title.toString().toLowerCase() : '';
              const input = inputValue.toLowerCase();
              return title.includes(input);
            }}
            treeData={groupTree}
            treeDefaultExpandAll
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          />
        </Form.Item>

        <Form.Item
          name="description"
          label="素材描述"
        >
          <TextArea 
            rows={3} 
            placeholder="请输入素材描述，便于后续查找和使用..."
            showCount
            maxLength={500}
          />
        </Form.Item>

        <Divider />

        {/* 素材内容 */}
        <Title level={5}>
          {currentTypeConfig?.label}内容
          <Text type="secondary" style={{ marginLeft: '8px', fontSize: '14px', fontWeight: 'normal' }}>
            根据素材类型填写相应内容
          </Text>
        </Title>

        {renderContentForm()}
      </Form>
    </Modal>

    {/* 封面素材选择器 */}
    <MaterialSelector
      visible={coverSelectorVisible}
      onCancel={() => setCoverSelectorVisible(false)}
      onSelect={handleSelectCoverMaterial}
      materialType="IMAGE"
      multiSelect={false}
      title="选择链接封面图片"
      tenantId={tenantId}
    />
    </>
  );
};

export default MaterialUploadModal;